import { useQuery } from '@apollo/client'
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import {
  LineChart,
  lineElementClasses,
  markElementClasses,
} from '@mui/x-charts/LineChart'
import dayjs from 'dayjs'

import TrendIndicator from '@/components/common/TrendIndicator'
import {
  DisplayCoolerProductHoverStatsQuery,
  DisplayCoolerProductHoverStatsQueryVariables,
  GET_DISPLAY_COOLER_PRODUCT_HOVER_STATS,
  GET_PRODUCTS,
  GetProductsQuery,
  GetProductsQueryVariables,
} from '@/graphql'
import { ProductHelpers } from '@/utils/products.helpers'

import { formatCurrencyOrDash } from '../utils/cooler-detail-formatters'

type Props = {
  displayId: string
  upc: string
  numFacings: number
  productsPerFacing: number
  startDate: string
  endDate: string
  initialInfo?: {
    preferredName: string | null
    revenue: number | null
    revenueTrend: number | null
    imageUrl: string | null
  }
}

const cardSx = {
  width: '100%',
  maxWidth: 340,
  borderRadius: 2,
}

const CoolerProductHoverCard: React.FC<Props> = ({
  displayId,
  upc,
  startDate,
  endDate,
  initialInfo,
  numFacings,
  productsPerFacing,
}) => {
  const theme = useTheme()
  const shouldSkipSeriesQuery = initialInfo?.revenue === null
  const { data, loading, error } = useQuery<
    DisplayCoolerProductHoverStatsQuery,
    DisplayCoolerProductHoverStatsQueryVariables
  >(GET_DISPLAY_COOLER_PRODUCT_HOVER_STATS, {
    variables: { displayId, upc, startDate, endDate },
    fetchPolicy: 'cache-first',
    skip: shouldSkipSeriesQuery,
  })
  const { data: productData } = useQuery<
    GetProductsQuery,
    GetProductsQueryVariables
  >(GET_PRODUCTS, {
    variables: {
      filters: { upcs: [upc] },
      pagination: { limit: 1, offset: 0 },
    },
    fetchPolicy: 'cache-first',
  })

  if (loading && !initialInfo) {
    return (
      <Card sx={cardSx}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={20} />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card sx={cardSx}>
        <CardContent>
          <Typography color="error" variant="body2">
            {error.message}
          </Typography>
        </CardContent>
      </Card>
    )
  }

  const stats = data?.displayCoolerProductHoverStats
  const product = productData?.products.results[0]
  const imageUrl =
    initialInfo?.imageUrl ||
    '/static/img/products/unknown-product-planogram-facing.png'
  const name =
    ProductHelpers.getProductPreferredName(product) ||
    initialInfo?.preferredName ||
    stats?.name ||
    upc
  const revenue = stats?.revenue ?? initialInfo?.revenue
  const revenueTrend = stats?.revenueTrend
  const revenueSeries = stats?.revenueSeries || []
  const seriesPoints = revenueSeries

  return (
    <Card sx={cardSx} variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={1.5}>
          <Box
            component="img"
            src={imageUrl}
            alt={name}
            sx={{
              width: 88,
              height: 118,
              objectFit: 'contain',
              borderRadius: 1,
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {name}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              UPC: {upc}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Revenue
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {formatCurrencyOrDash(revenue)}
              </Typography>
              {revenueTrend !== null && revenueTrend !== undefined && (
                <TrendIndicator
                  value={revenueTrend}
                  prefix="$"
                  suffix="/month"
                />
              )}
            </Stack>
            {stats?.averageVolumePerDay !== null &&
              stats?.averageVolumePerDay !== undefined && (
                <Typography variant="body2" color="text.secondary">
                  Volume: {stats.averageVolumePerDay.toFixed(1)} /day
                </Typography>
              )}
            {stats?.averageVolumePerDay !== null &&
              stats?.averageVolumePerDay !== undefined && (
                <Typography variant="body2" color="text.secondary">
                  Days Inventory:{' '}
                  {(
                    (numFacings * productsPerFacing) /
                    stats.averageVolumePerDay
                  ).toFixed(1)}
                </Typography>
              )}
          </Box>
        </Stack>

        {loading && !stats ? (
          <Box sx={{ py: 1.5, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={16} />
          </Box>
        ) : revenueSeries.length > 0 ? (
          <Box sx={{ width: '100%', overflow: 'hidden' }}>
            <LineChart
              slotProps={{
                // Default ChartsTooltip uses theme.zIndex.modal; StyledPopper (planogram hover) uses modal+1.
                tooltip: { sx: { zIndex: theme.zIndex.tooltip } },
              }}
              sx={{
                [`& .${lineElementClasses.root}`]: {
                  strokeWidth: 2,
                },
                [`& .${markElementClasses.root}`]: {
                  r: 2, // Modify the circle radius
                  strokeWidth: 2,
                },
              }}
              height={128}
              margin={{ top: 8, right: 50, bottom: 26, left: 28 }}
              xAxis={[
                {
                  scaleType: 'time',
                  data: seriesPoints.map(point => dayjs(point.date).toDate()),
                  valueFormatter: (value: Date) =>
                    dayjs(value).format("MMM 'YY"),
                  min:
                    seriesPoints.length > 0
                      ? dayjs(seriesPoints[0].date).toDate()
                      : undefined,
                  max:
                    seriesPoints.length > 0
                      ? dayjs(
                          seriesPoints[seriesPoints.length - 1].date,
                        ).toDate()
                      : undefined,
                },
              ]}
              yAxis={[
                {
                  valueFormatter: (value: number | null) =>
                    value === null || value === undefined
                      ? '-'
                      : formatCurrencyOrDash(value),
                },
              ]}
              series={[
                {
                  data: seriesPoints.map(point => point.revenue as number),
                  valueFormatter: (value: number | null) =>
                    value === null || value === undefined
                      ? '-'
                      : formatCurrencyOrDash(value),
                },
              ]}
              grid={{ horizontal: true, vertical: true }}
            />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No revenue series data for selected range
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

export default CoolerProductHoverCard
